import { MixpanelEventContext } from '@/constants/mixpanel';
import { LabelStatus, PrintReportStatus } from '@/constants/shipments';
import { snakeToCamelCase } from '@/utils/snakeCamelTransform';
import { IReport, LabelJob, ReportType } from '../types';

const DEFAULT_LABEL_STATUS = LabelStatus.PENDING;

type Listener = () => void;

const newLabelJob = (shipmentIds: string[], context: MixpanelEventContext): LabelJob => ({
  id: shipmentIds[0] as string,
  time: new Date(),
  context,
  shipments: shipmentIds.map((id) => ({
    id,
    status: DEFAULT_LABEL_STATUS,
    time: new Date(),
  })),
});

const newReportJobs = (
  id: string,
  context: MixpanelEventContext,
  reportIds?: string[]
): LabelJob => ({
  id,
  time: new Date(),
  context,
  reports: reportIds
    ? reportIds.map(
        (reportId) =>
          ({
            id: reportId,
            state: PrintReportStatus.PENDING,
            time: new Date(),
          } as unknown as ReportType)
      )
    : undefined,
  shipments: [],
});

const createStore = () => {
  const StoreData = {
    labelJobs: [] as LabelJob[],
    listeners: new Set<Listener>(),
  } as {
    labelJobs: LabelJob[];
    listeners: Set<Listener>;
  };

  const notifyListeners = () => {
    StoreData.listeners.forEach((notify) => notify());
  };

  const refreshStore = (newJobs = StoreData.labelJobs) => {
    StoreData.labelJobs = [...newJobs];
    notifyListeners();
  };

  return {
    getLabelJobs: () => StoreData.labelJobs,
    subscribe: (listener: Listener) => {
      StoreData.listeners.add(listener);
      return () => {
        StoreData.listeners.delete(listener);
      };
    },

    refreshJobs: () => {
      refreshStore();
    },
    removeJob: (id: string) => {
      const index = StoreData.labelJobs.findIndex((job) => job.id === id);
      if (index === -1) return;
      const data = [...StoreData.labelJobs];
      data.splice(index, 1);
      refreshStore(data);
    },

    updateJobLabelState: (id: string, status: LabelStatus) => {
      const labelJobIndex = StoreData.labelJobs.findIndex(
        (job) => !!job.shipments.find((shipment) => shipment.id === id)
      );
      if (labelJobIndex === -1) return;

      const labelJob = StoreData.labelJobs[labelJobIndex];
      const shipmentIndex = labelJob.shipments.findIndex((shipment) => shipment.id === id);
      if (shipmentIndex === -1) return;
      const shipment = labelJob.shipments[shipmentIndex];
      if (shipment.status === status) return;
      const shipments = [...labelJob.shipments];
      shipments[shipmentIndex] = {
        ...shipment,
        time: new Date(),
        status,
      };
      const labelJobs = [...StoreData.labelJobs];
      labelJobs[labelJobIndex] = {
        ...labelJob,
        time: new Date(),
        shipments,
      };
      refreshStore(labelJobs);
    },

    updateJobReport: (report: IReport) => {
      const jobQueueIndex = StoreData.labelJobs.findIndex((job) =>
        job.reports?.find((r) => r.id === report.id)
      );
      if (jobQueueIndex === -1) return false;
      const jobWithReport = StoreData.labelJobs[jobQueueIndex];
      if (!jobWithReport.reports) return false;
      const reportQueueIndex = jobWithReport.reports.findIndex((r) => r.id === report.id);
      if (reportQueueIndex === -1) return false;
      const reports = [...jobWithReport.reports];
      reports.splice(reportQueueIndex, 1, {
        ...snakeToCamelCase(report),
        time: new Date(),
      });
      StoreData.labelJobs[jobQueueIndex] = {
        ...StoreData.labelJobs[jobQueueIndex],
        reports: [...reports],
        time: new Date(),
      };
      refreshStore();
      return true;
    },

    addJob: (shipmentIds: string[], context: MixpanelEventContext) => {
      refreshStore([...StoreData.labelJobs, newLabelJob(shipmentIds, context)]);
    },

    addJobReport: (id: string, context: MixpanelEventContext, reportIds?: string[]) => {
      const indexOfJob = StoreData.labelJobs.findIndex((job) => job.id === id);
      if (indexOfJob === -1) return;
      StoreData.labelJobs[indexOfJob] = newReportJobs(id, context, reportIds);
    },

    addLabelJobObjectToQueue: (job: LabelJob) => {
      if (!job?.id) return;
      const indexOfJob = StoreData.labelJobs.findIndex((j) => job.id === j.id);
      if (indexOfJob !== -1) return;
      StoreData.labelJobs = [...StoreData.labelJobs, job];
    },
  };
};

const store = createStore();

export default store;

export const addLabelJobToQueue = (ids: string[], context: MixpanelEventContext) => {
  if (!ids.length) return;
  store.addJob(ids, context);
};

export const addReportJobToQueue = (
  id: string,
  context: MixpanelEventContext,
  reportId?: string[]
) => {
  store.addJobReport(id, context, reportId);
};

export const addLabelJobObjectToQueue = (job: LabelJob) => {
  store.addLabelJobObjectToQueue(job);
};

export const updateJobReportOnQueue = (report: IReport) => store.updateJobReport(report);

export const subscribeToLabelJobsQueue = (listener: Listener) => {
  return store.subscribe(listener);
};

export const updateJobLabelStateOnQueue = (id: string, status: LabelStatus) => {
  store.updateJobLabelState(id, status);
};

export const removeLabelJobFromQueue = (id: string) => {
  store.removeJob(id);
};

export const refreshLabelJobsQueue = () => {
  store.refreshJobs();
};
